import React, { useEffect, useState, useRef, useCallback } from "react";
import { Marker, Source, Layer } from "react-map-gl";
import Map from "../../MapWhenReady/MapWhenReady";
import "mapbox-gl/dist/mapbox-gl.css";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { rider as riderQuery, subscriptionRiderLocation } from "../../../apollo/server";
import ConfigurableValues from "../../../config/constants";
import { getDirections, distanceMeters } from "../../../utils/mapbox";
import RestMarker from "../../../assets/images/rest-map-2.png";
import DestMarker from "../../../assets/images/dest-map-2.png";
import RiderMarker from "../../../assets/images/rider-map-2.png";

const RIDER = gql`${riderQuery}`;
const RIDER_LOCATION = gql`${subscriptionRiderLocation}`;

// Show rider→restaurant route before pickup, restaurant→customer after.
const PRE_PICKUP_STATUSES = ["ASSIGNED", "ARRIVED_PICKUP"];

const ROUTE_LAYER = {
  id: "route-line",
  type: "line",
  layout: { "line-join": "round", "line-cap": "round" },
  paint: { "line-color": "#16A34A", "line-width": 5, "line-opacity": 0.85 },
};

const LiveTrackingMap = ({ orderStatus, restCoordinates, destCoordinates, riderId }) => {
  const { MAPBOX_TOKEN } = ConfigurableValues();
  const { data, subscribeToMore } = useQuery(RIDER, {
    variables: { id: riderId },
    fetchPolicy: "network-only",
  });
  const [routeGeojson, setRouteGeojson] = useState(null);
  const mapRef = useRef(null);
  const lastLegRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeToMore({
      document: RIDER_LOCATION,
      variables: { riderId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return {
          rider: { ...prev.rider, ...subscriptionData.data.subscriptionRiderLocation },
        };
      },
    });
    return unsub;
  }, [riderId, subscribeToMore]);

  const riderCoords = data?.rider?.location?.coordinates
    ? { lat: parseFloat(data.rider.location.coordinates[1]), lng: parseFloat(data.rider.location.coordinates[0]) }
    : null;

  // Keep the route line anchored to the LIVE rider position so it shrinks as the
  // rider moves. Origin is always the rider; destination is the restaurant
  // before pickup, the customer after. Recompute when the leg changes OR the
  // rider has moved a meaningful distance (~40 m), not on every GPS tick — that
  // would hammer the Directions API and jitter the line.
  const lastRouteOriginRef = useRef(null);
  useEffect(() => {
    const isPrePickup = PRE_PICKUP_STATUSES.includes(orderStatus);
    const leg = isPrePickup ? "pre" : "post";
    const origin = riderCoords;
    const dest = isPrePickup ? restCoordinates : destCoordinates;
    if (!origin || !dest || origin.lat == null || dest.lat == null) return;

    const legChanged = lastLegRef.current !== leg;
    const movedEnough =
      !lastRouteOriginRef.current ||
      distanceMeters(lastRouteOriginRef.current, origin) > 40;
    if (!legChanged && !movedEnough && routeGeojson) return;

    lastLegRef.current = leg;
    lastRouteOriginRef.current = origin;
    let cancelled = false;
    getDirections(origin, dest, MAPBOX_TOKEN)
      .then((res) => {
        if (!cancelled && res) setRouteGeojson(res.geojson);
      })
      .catch((e) => console.warn("[LiveTrackingMap] directions error", e?.message));
    return () => {
      cancelled = true;
    };
  }, [orderStatus, riderCoords, restCoordinates, destCoordinates, routeGeojson, MAPBOX_TOKEN]);

  // Fit the map to the active leg (rider + current destination), capping zoom so
  // it never zooms in too far when the two points are close together.
  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const isPrePickup = PRE_PICKUP_STATUSES.includes(orderStatus);
    const activeDest = isPrePickup ? restCoordinates : destCoordinates;
    const pts = [riderCoords, activeDest].filter((p) => p && p.lat != null);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 15, duration: 500 });
      return;
    }
    const lngs = pts.map((p) => p.lng);
    const lats = pts.map((p) => p.lat);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 70, maxZoom: 16, duration: 500 }
    );
  }, [orderStatus, restCoordinates, destCoordinates, riderCoords]);

  useEffect(() => {
    fitBounds();
  }, [fitBounds]);

  const center = restCoordinates || destCoordinates || riderCoords || { lat: 0, lng: 0 };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 13 }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        onLoad={fitBounds}
      >
        {restCoordinates && (
          <Marker longitude={restCoordinates.lng} latitude={restCoordinates.lat} anchor="bottom">
            <img src={RestMarker} alt="restaurant" style={{ width: 32, height: 32 }} />
          </Marker>
        )}
        {destCoordinates && (
          <Marker longitude={destCoordinates.lng} latitude={destCoordinates.lat} anchor="bottom">
            <img src={DestMarker} alt="destination" style={{ width: 32, height: 32 }} />
          </Marker>
        )}
        {riderCoords && (
          <Marker longitude={riderCoords.lng} latitude={riderCoords.lat} anchor="center">
            <img src={RiderMarker} alt="rider" style={{ width: 36, height: 36 }} />
          </Marker>
        )}
        {routeGeojson && (
          <Source id="route" type="geojson" data={routeGeojson}>
            <Layer {...ROUTE_LAYER} />
          </Source>
        )}
      </Map>
    </div>
  );
};

export default LiveTrackingMap;
